'use client'

import { t } from '@lingui/core/macro'
import { useCallback, useEffect, useMemo } from 'react'
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import {
  DeviceLabels,
  MeetingManagerJoinOptions,
  MeetingStatus,
  useMeetingManager,
  useMeetingStatus,
  useMeetingEvent,
  useRosterState,
} from 'amazon-chime-sdk-component-library-react'
import { Box, Chip } from '@mui/material'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'

import { useGetChimeMeetingLazyQuery, useUpdateConsultationStatusMutation } from '@/core/graphql/hooks'
import { ConsultationStatus, ChimeMeetingInfo, ChimeAttendeeInfo } from '@/core/graphql/types'
import { useIsFR } from '@/hooks/useIsFR'
import { NoRemoteVideo } from '@/shared/ui/meeting/NoRemoteVideo'
import { VideoTileGrid } from '@/shared/ui/meeting/VideoTileGrid'
import { AudioInputControl } from '@/shared/ui/meeting/controls/AudioInputControl'
import { AudioOutputControl } from '@/shared/ui/meeting/controls/AudioOutputControl'
import { DeviceSelectionControl } from '@/shared/ui/meeting/controls/DeviceSelectionControl'
import { EndConsultationControl } from './EndConsultationControl'
import { LocalVideoControl } from '@/shared/ui/meeting/controls/LocalVideoControl'
import { ScreenShareControl } from '@/shared/ui/meeting/controls/ScreenshareControl'
import { PatientLocationControl } from '@/features/patient/location'
import { RecordingConsentDialog } from '@/shared/ui/meeting/RecordingConsentDialog'

import { useRecordingConsent } from '../model/useRecordingConsent'
import { Documents } from './Documents'

type VisioConferenceMeetingProps = {
  consultationStatus: ConsultationStatus
  doctorName?: string
  doctorRole?: string
  patientId: string
  room: string
  consultationId: string
  autoRecording?: boolean
  recordingConsentPending?: boolean
  recordingConsent?: boolean
  initialPatientLocation?: {
    description?: string | null
    placeId?: string | null
  } | null
}

export function VisioConferenceMeeting(props: VisioConferenceMeetingProps) {
  const {
    consultationStatus,
    doctorName,
    doctorRole,
    patientId,
    room,
    consultationId,
    autoRecording,
    recordingConsentPending,
    recordingConsent,
    initialPatientLocation,
  } = props

  const meetingEvent = useMeetingEvent()
  const meetingManager = useMeetingManager()
  const meetingStatus = useMeetingStatus()
  const { roster } = useRosterState()
  const isFR = useIsFR()

  const attendees = useMemo(() => Object.values(roster), [roster])

  const isRecording = useMemo(() => attendees.some((attendee) => attendee.externalUserId?.startsWith('aws:MediaPipeline')), [attendees])

  const isRemotePresent = useMemo(
    () => attendees.some((a) => a.externalUserId && a.externalUserId !== patientId && !a.externalUserId.startsWith('aws:MediaPipeline')),
    [attendees, patientId]
  )

  const { consentRequested, respondToConsent } = useRecordingConsent({
    consultationId,
    patientId,
    autoRecording,
    recordingConsentPending,
    recordingConsent,
  })

  const [getChimeMeeting, { called, data, loading, refetch }] = useGetChimeMeetingLazyQuery({
    variables: { meetingType: 'visioConference', roomOrConsultationId: room, userId: patientId },
  })
  const [updateConsultationStatus] = useUpdateConsultationStatusMutation()

  const attendeeInfo = data?.chimeMeeting.attendeeInfo
  const meetingInfo = data?.chimeMeeting.meetingInfo

  const startMeeting = useCallback(
    async (meeting: ChimeMeetingInfo, attendee: ChimeAttendeeInfo) => {
      const config = new MeetingSessionConfiguration(meeting, attendee)
      const options: MeetingManagerJoinOptions = { deviceLabels: DeviceLabels.AudioAndVideo, enableWebAudio: true }
      await meetingManager.join(config, options)
      await meetingManager.start()
    },
    [meetingManager]
  )

  useEffect(() => {
    if (!called) getChimeMeeting()
  }, [called, getChimeMeeting])

  useEffect(() => {
    if (meetingInfo && attendeeInfo) startMeeting(meetingInfo, attendeeInfo)
  }, [startMeeting, attendeeInfo, meetingInfo])

  useEffect(() => {
    if (meetingEvent?.name === 'meetingEnded' && meetingStatus !== MeetingStatus.Left && meetingStatus !== MeetingStatus.JoinedFromAnotherDevice) {
      refetch()
    }
  }, [meetingEvent, meetingStatus, refetch])

  useEffect(() => {
    const participantCount = attendees.filter((a) => a.externalUserId && !a.externalUserId.startsWith('aws:MediaPipeline')).length

    if (consultationStatus !== ConsultationStatus.Active && participantCount >= 2) {
      updateConsultationStatus({ variables: { payload: { consultationId: room, status: ConsultationStatus.Active } } })
    }
  }, [attendees, consultationStatus, room, updateConsultationStatus])

  if (loading) return <NoRemoteVideo participantName={doctorName} participantRole={doctorRole} isRemotePresent={isRemotePresent} />

  return (
    <Box sx={{ flex: 1, minHeight: 0, width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          '& video': {
            objectFit: { xs: 'cover', md: 'contain' },
            width: '100%',
            height: '100%',
          },
        }}
      >
        {isRecording && (
          <Chip
            icon={<RadioButtonCheckedIcon sx={{ fontSize: 14 }} />}
            label={t`Recording in progress`}
            color="error"
            size="small"
            sx={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}
          />
        )}
        <VideoTileGrid
          layout="featured"
          noRemoteVideoView={<NoRemoteVideo participantName={doctorName} participantRole={doctorRole} isRemotePresent={isRemotePresent} />}
        />
      </Box>

      {meetingStatus === MeetingStatus.Succeeded && (
        <Box
          sx={{ bgcolor: 'grey.800', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.5 }}
          role="toolbar"
          aria-label="Consultation controls"
        >
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <AudioInputControl />
            <LocalVideoControl />
            <AudioOutputControl />
            <ScreenShareControl />
            {isFR && <PatientLocationControl consultationId={consultationId} initialLocation={initialPatientLocation} />}
            <DeviceSelectionControl initialOpen={true} />
            <EndConsultationControl consultationId={consultationId} />
          </Box>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', mr: 1.5 }}>
            <Documents consultationId={consultationId} />
          </Box>
        </Box>
      )}

      {consentRequested && <RecordingConsentDialog onAccept={() => respondToConsent(true)} onRefuse={() => respondToConsent(false)} />}
    </Box>
  )
}
